import React, { useEffect } from "react";
import numeral from 'numeral';
import {
  Container,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core";
import SwapVerticalCircleIcon from "@material-ui/icons/SwapVerticalCircle";
import { useSnackbar } from "notistack";
import LoopIcon from "@material-ui/icons/Loop";
import * as COLORS from "@material-ui/core/colors";

import {
  getAccount,
  getFactory,
  getProvider,
  getRouter,
  getSigner,
  getNetwork,
  getAmountOut,
  getBalanceAndSymbol,
  getWeth,
  swapTokens,
  getReserves,
} from "../ethereumFunctions";
import CoinField from "./CoinField";
import CoinDialog from "./CoinDialog";
import LoadingButton from "../Components/LoadingButton";
import WrongNetwork from "../Components/wrongNetwork";
import COINS from "../constants/coins";
import * as chains from "../constants/chains";


const styles = (theme) => ({
  paperContainer: {
    backgroundColor: "rgba(37, 44, 69, 0.8)",
    color: COLORS.green[50],
    borderRadius: theme.spacing(2),
    padding: theme.spacing(1),
    paddingBottom: theme.spacing(3),
    border: "2px solid #00ff00", 
    boxShadow: "0 0 8px 4px rgba(0, 174, 255, 0.6)", 
    transition: "box-shadow 0.3s ease-in-out",
    "&:hover": {
      boxShadow: "0 0 12px 6px rgba(0, 174, 255, 0.6)", 
      
    },
  },
  
  switchButton: {
    zIndex: 1,
    margin: "-16px",
    padding: theme.spacing(0.8),
    backgroundColor: COLORS.green[50],
  },

  fullWidth: {
    width: "100%",
  },

  title: {
    textAlign: "left",
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1), 
    
  },

  hr: {
    width: "100%",
  },

  balance: {
    padding: theme.spacing(1),
    overflowWrap: "break-word",
    textAlign: "center",
  },

  footer: {
    marginTop: "285px",
  },
});

const useStyles = makeStyles(styles);

function CoinSwapper(props) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  // Stores a record of whether their respective dialog window is open
  const [dialog1Open, setDialog1Open] = React.useState(false);
  const [dialog2Open, setDialog2Open] = React.useState(false);
  const [wrongNetworkOpen, setwrongNetworkOpen] = React.useState(false);

  // Stores data about their respective coin
  const [coin1, setCoin1] = React.useState({
    address: undefined,
    symbol: undefined,
    balance: undefined,
  });
  const [coin2, setCoin2] = React.useState({
    address: undefined,
    symbol: undefined,
    balance: undefined,
  });

  // Stores the current reserves in the liquidity pool between coin1 and coin2
  const [reserves, setReserves] = React.useState(["0.0", "0.0"]);

  // Stores the current value of their respective text box
  const [field1Value, setField1Value] = React.useState("");
  const [field2Value, setField2Value] = React.useState("");

  // Controls the loading button
  const [loading, setLoading] = React.useState(false);

  // Switches the top and bottom coins, this is called when users hit the swap button or select the opposite
  // token in the dialog (e.g. if coin1 is TokenA and the user selects TokenB when choosing coin2)
  const switchFields = () => {
    setCoin1(coin2);
    setCoin2(coin1);
    setField1Value(field2Value);
    setReserves(reserves.reverse());
  };

  // These functions take an HTML event, pull the data out and puts it into a state variable.
  const handleChange = {
    field1: (e) => {
      setField1Value(e.target.value);
    },
  };

  // Turns the coin's reserves into something nice and readable
  const formatReserve = (reserve, symbol) => {
    if (reserve && symbol) return reserve + " " + symbol;
    else return "0.0";
  };

  // Determines whether the button should be enabled or not
  const isButtonEnabled = () => {

    // If both coins have been selected, and a valid float has been entered which is less than the user's balance, then return true
    const parsedInput1 = parseFloat(field1Value);
    const parsedInput2 = parseFloat(field2Value);
    return (
      coin1.address &&
      coin2.address &&
      !isNaN(parsedInput1) &&
      !isNaN(parsedInput2) &&
      0 < parsedInput1 &&
      parsedInput1 <= coin1.balance
    );
  };

  const onTokenSelected = (address, isCoin1) => {
  if (isCoin1) {
    setDialog1Open(false);
    if (address === coin2.address) {
      switchFields();
    } else if (address) {
      getBalanceAndSymbol(props.network.account, address, props.network.provider, props.network.signer, props.network.weth.address, props.network.coins).then((data) => {
        setCoin1({
          address: address,
          symbol: data.symbol,
          balance: data.balance,
        });
      });
    }
  } else {
    setDialog2Open(false);
    if (address === coin1.address) {
      switchFields();
    } else if (address) {
      getBalanceAndSymbol(props.network.account, address, props.network.provider, props.network.signer, props.network.weth.address, props.network.coins).then((data) => {
        setCoin2({
          address: address,
          symbol: data.symbol,
          balance: data.balance,
        });
      });
    }
  }
};

// Update calls to onTokenSelected in your dialogs
const onToken1Selected = (address) => onTokenSelected(address, true);
const onToken2Selected = (address) => onTokenSelected(address, false);

  const swap = () => {
    console.log("Attempting to swap tokens...");
    setLoading(true);
  
    swapTokens(
      coin1.address,
      coin2.address,
      field1Value,
      props.network.router,
      props.network.account,
      enqueueSnackbar // Pass the enqueueSnackbar function for error/success messages
    )
      .then((result) => {
        setLoading(false);
  
        if (result.success) {
          // If the transaction was successful, clear the input to prevent accidental re-transfers
          setField1Value("");
          enqueueSnackbar("Transaction Successful", { variant: "success" });
        } else {
          // Handle the case where swapTokens indicates a failure but doesn't throw an error
          enqueueSnackbar(`Transaction Failed: ${result.message}`, {
            variant: "error",
            autoHideDuration: 5000,
          });
        }
      })
      .catch((e) => {
        setLoading(false);
        console.error("Error in swap function:", e);
        enqueueSnackbar("Transaction Failed", {
          variant: "error",
          autoHideDuration: 5000,
        });
      });
  };
  

  // The lambdas within these useEffects will be called when a particular dependency is updated. These dependencies
  // are defined in the array of variables passed to the function after the lambda expression. If there are no dependencies
  // the lambda will only ever be called when the component mounts. These are very useful for calculating new values
  // after a particular state change, for example, calculating the new exchange rate whenever the addresses
  // of the two coins change.

  // This hook is called when either of the state variables `coin1.address` or `coin2.address` change.
  // This means that when the user selects a different coin to convert between, or the coins are swapped,
  // the new reserves will be calculated.
  useEffect(() => {
    console.log(
      "Trying to get Reserves between:\n" + coin1.address + "\n" + coin2.address
    );

    if (coin1.address && coin2.address) {
      getReserves(coin1.address, coin2.address, props.network.factory, props.network.signer, props.network.account).then(
        (data) => setReserves(data)
      );
    }
  }, [coin1.address, coin2.address, props.network.account, props.network.factory, props.network.router, props.network.signer]);

  // This hook is called when either of the state variables `field1Value` `coin1.address` or `coin2.address` change.
  // It attempts to calculate and set the state variable `field2Value`
  // This means that if the user types a new value into the conversion box or the conversion rate changes,
  // the value in the output box will change.
  useEffect(() => {
    if (isNaN(parseFloat(field1Value))) {
      setField2Value("");
    } else if (parseFloat(field1Value) && coin1.address && coin2.address) {
      getAmountOut(coin1.address, coin2.address, field1Value, props.network.router, props.network.signer).then(
        (amount) => setField2Value(amount.toFixed(7))
      ).catch(e => {
        console.log(e);
        setField2Value("NA");
      })
    } else {
      setField2Value("");
    }
  }, [field1Value, coin1.address, coin2.address]);

  // This hook creates a timeout that will run every ~10 seconds, it's role is to check if the user's balance has
  // updated has changed. This allows them to see when a transaction completes by looking at the balance output.
  useEffect(() => {
    const coinTimeout = setTimeout(() => {
      console.log('props: ', props);
      console.log("Checking balances...");

      if (coin1.address && coin2.address && props.network.account) {
        getReserves(
          coin1.address,
          coin2.address,
          props.network.factory,
          props.network.signer,
          props.network.account
        ).then((data) => setReserves(data));
      }

      if (coin1.address && props.network.account &&!wrongNetworkOpen) {
        getBalanceAndSymbol(
          props.network.account,
          coin1.address,
          props.network.provider,
          props.network.signer,
          props.network.weth.address,
          props.network.coins
          ).then(
          (data) => {
            setCoin1({
              ...coin1,
              balance: data.balance,
            });
          }
        );
      }
      if (coin2.address && props.network.account &&!wrongNetworkOpen) {
        getBalanceAndSymbol(
          props.network.account,
          coin2.address,
          props.network.provider,
          props.network.signer,
          props.network.weth.address,
          props.network.coins
          ).then(
          (data) => {
            setCoin2({
              ...coin2,
              balance: data.balance,
            });
          }
        );
      }
    }, 30000);

    return () => clearTimeout(coinTimeout);
  });

  return (
    <div>
      {/* Dialog Windows */}
      <CoinDialog
        open={dialog1Open}
        onClose={onToken1Selected}
        coins={props.network.coins}
        props={props.network.signer}
      />
      <CoinDialog
        open={dialog2Open}
        onClose={onToken2Selected}
        coins={props.network.coins}
        signer={props.network.signer}
      />
      <WrongNetwork
        open={wrongNetworkOpen}
        />

      {/* Coin Swapper */}
      <Container maxWidth="xs">
        <Paper className={classes.paperContainer}>
        <Grid container alignItems="center">
  <Grid item>
    <div style={{ borderRadius: '20%', overflow: 'hidden', width: '50px', height: '50px', marginRight: '10px', marginLeft: '10px', marginTop: '10px',  }}>
      <img src="exe.gif" alt="DexTop Icon" style={{ width: '100%', height: 'auto' }} />
    </div>
    
  </Grid>
  <Grid item>
  <h5 className="navbar-logo"> DexTop 
</h5>

  </Grid>
</Grid>
<br></br>

<Grid container direction="column" alignItems="center" spacing={2}>
  <Grid item xs={12} className={classes.fullWidth}>
    <CoinField
      activeField={true}
      value={field1Value}
      onClick={() => setDialog1Open(true)}
      onChange={handleChange.field1}
      symbol={
        <span style={{ fontFamily: "'Press Start 2P'", fontSize: "12px"  }}>
          {coin1.symbol !== undefined ? coin1.symbol : "Select"}
        </span>
      }
    />
  </Grid>

  <IconButton onClick={switchFields} className={classes.switchButton}>
    <SwapVerticalCircleIcon fontSize="medium" />
  </IconButton>

  <Grid item xs={12} className={classes.fullWidth}>
    <CoinField
      activeField={false}
      value={field2Value}
      onClick={() => setDialog2Open(true)}
      symbol={
        <span style={{ fontFamily: "'Press Start 2P'", fontSize: "12px"  }}>
          {coin2.symbol !== undefined ? coin2.symbol : "Select"}
        </span>
      }
    />
  </Grid>



            <hr className={classes.hr} />

            {/* Balance Display */}
            <Typography variant="h6">Your Balances</Typography>
<Grid container direction="row" justifyContent="space-between">
  <Grid item xs={6}>
    <Typography variant="body1" className={classes.balance}>
      {coin1.balance !== undefined ? `${numeral(coin1.balance).format('0,0.000')} ${coin1.symbol}` : "0.00"}
    </Typography>
  </Grid>
  <Grid item xs={6}>
    <Typography variant="body1" className={classes.balance}>
      {coin2.balance !== undefined ? `${numeral(coin2.balance).format('0,0.000')} ${coin2.symbol}` : "0.00"}
    </Typography>
  </Grid>
</Grid>



            <hr className={classes.hr} />

            {/* Reserves Display */}
<Typography variant="h6">Reserves</Typography>
<Grid container direction="row" justifyContent="space-between">
  <Grid item xs={6}>
    <Typography variant="body1" className={classes.balance}>
      {formatReserve(numeral(reserves[0]).format('0,0.000'), coin1.symbol)}
    </Typography>
  </Grid>
  <Grid item xs={6}>
    <Typography variant="body1" className={classes.balance}>
      {formatReserve(numeral(reserves[1]).format('0,0.000'), coin2.symbol)}
    </Typography>
  </Grid>
</Grid>

            <hr className={classes.hr} />

            <LoadingButton
              loading={loading}
              valid={isButtonEnabled()}
              success={false}
              fail={false}
              onClick={swap}
            >
              <LoopIcon />
              Swap
            </LoadingButton>
          </Grid>
        </Paper>
      </Container>

    {/* NOT USED
  container
  className={classes.footer}
  direction="column"
  justifyContent="center"
  alignItems="center"
  spacing={2}
  style={{ maxWidth: '100%', margin: '0 auto'}} 
>
  <Grid item>
    <p style={{ textAlign: 'center', color: 'white' }}>
      <a href="https://dextop.pro/" style={{ color: 'white' }}>DEXTOP</a>
    </p>
  </Grid>
  <Grid item>
    <p style={{ textAlign: 'center', color: 'white' }}>
      <a href="https://internetmoney.io/" style={{ color: 'white' }}>IM WALLET</a>
    </p>
  </Grid>
</Grid> */}
  </div>
  );
}

export default CoinSwapper;